import { Fragment, useState, useEffect, useCallback, useMemo, useRef } from 'react'
import axios from 'axios'
import {
  Box, Stack, Typography, Button, IconButton, useMediaQuery, useTheme,
  Table, TableHead, TableBody, TableRow, TableCell, CircularProgress,
  Collapse, Alert, Tooltip,
} from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import WarningIcon from '@mui/icons-material/Warning'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import PageContainer from '../components/common/PageContainer'
import ViewHeader from '../components/ViewHeader'
import Panel from '../components/common/Panel'
import RefreshButton from '../components/common/RefreshButton'
import ListToolbar from '../components/common/ListToolbar'
import FilterPillRow from '../components/common/FilterPillRow'
import TableViewport from '../components/common/TableViewport'
import MobileRecordCard from '../components/common/MobileRecordCard'
import RowActionsMenu from '../components/common/RowActionsMenu'
import EmptyState from '../components/common/EmptyState'
import useListQuery from '../components/common/listQuery/useListQuery'
import useColumnPriority from '../components/common/useColumnPriority'
import useContainerWidth from '../components/common/useContainerWidth'
import TableSkeleton from '../Skeletons/TableSkeleton'
import { CONTENT_WIDTH, COLUMN_MAX, FS, SP } from '../theme/tokens'

const API = process.env.REACT_APP_API_BASE_URL || ''

/**
 * Die fünf Quick-Filter-Werte. `never` ist ein EIGENER Wert, kein `null`:
 * er entsteht aus `c.latest?.status || 'never'` und wird als `never` in die
 * Adresse geschrieben (Plan G14b §2.2 Nr. 3, §3.1).
 */
const QUICK_VALUES = ['ok', 'fail', 'warn', 'skipped', 'never']

const QUICK_LABEL = {
  ok: 'OK',
  fail: 'Failing',
  warn: 'Warning',
  skipped: 'Skipped',
  never: 'Never run',
}

const STATUS_LABEL = {
  ok: 'OK',
  fail: 'Failing',
  warn: 'Warning',
  skipped: 'Skipped',
  never: 'Never run',
}

/** Die Statusfarbe — semantisch, nie ein Literal (Regel 22). */
const STATUS_COLOR = {
  ok: 'success.main',
  fail: 'error.main',
  warn: 'warning.main',
  skipped: 'text.disabled',
  never: 'text.disabled',
}

// 🔴 W9 (Regel-16-Runde 2): „Medium" stand in `warning.main` (#ed6c02) bei
// `caption`-Groesse — rund 3:1 auf weisser Flaeche, unter den 4,5:1 fuer Text.
// Die Information haengt nicht an der Farbe (das Wort traegt sie), deshalb ist
// „Medium" jetzt wie „Low" `text.secondary`; „High" bleibt `error.main`.
const RISK_COLOR = { high: 'error.main', medium: 'text.secondary', low: 'text.secondary' }

/**
 * Die Risikostufe als WORT (Regel-16-Runde 1, W2). Bis dahin stand der rohe
 * Aufzaehlungswert („high") in einer Spalte namens „Risk", waehrend jeder
 * andere Zustand der Seite benannt wird („OK", „Failing", „Skipped").
 */
const RISK_LABEL = { high: 'High', medium: 'Medium', low: 'Low' }

/**
 * Die Spalten der Tabelle — Deskriptoren für `useColumnPriority` (G0b).
 * `type` steht auf `xl` (1360): bei der Lesebreite 1280 fällt sie, ab ~1400
 * Behältnis ist sie sichtbar. Sie ist die einzige Spalte ohne Handlungsbezug
 * (der Typ steht auch in der Detailzeile), Plan §2.2 Nr. 7.
 */
const SPALTEN = [
  { key: 'expand', priority: 'kern', kind: 'number', width: 48 },
  { key: 'status', priority: 'kern', kind: 'chip', width: 132 },
  { key: 'name', priority: 'kern', kind: 'identity' },
  { key: 'type', priority: 'xl', kind: 'chip', width: 110 },
  { key: 'risk', priority: 'kern', kind: 'chip', width: 96 },
  { key: 'lastrun', priority: 'kern', kind: 'date', width: 148 },
  { key: 'streak', priority: 'kern', kind: 'number', width: 84 },
  { key: 'actions', priority: 'kern', kind: 'number', width: 150 },
]

const SPALTE_VON = Object.fromEntries(SPALTEN.map((s) => [s.key, s]))

function spaltenBreite(key) {
  const sp = SPALTE_VON[key]
  return sp?.width ? { width: sp.width } : undefined
}

function StatusAnzeige({ status }) {
  const wert = status || 'never'
  const label = STATUS_LABEL[wert] || wert
  const farbe = STATUS_COLOR[wert] || 'text.disabled'
  return (
    <Stack direction="row" spacing={SP.chip} alignItems="center">
      <Box component="span" role="img" aria-label={label} sx={{ display: 'inline-flex', color: farbe }}>
        {wert === 'ok' && <CheckCircleIcon sx={{ fontSize: FS.xl }} />}
        {wert === 'fail' && <ErrorIcon sx={{ fontSize: FS.xl }} />}
        {wert === 'warn' && <WarningIcon sx={{ fontSize: FS.xl }} />}
        {(wert === 'skipped' || wert === 'never') && <HelpOutlineIcon sx={{ fontSize: FS.xl }} />}
      </Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Stack>
  )
}

function formatRelative(iso) {
  if (!iso) return 'never'
  const diff = (Date.now() - new Date(iso).getTime()) / 1000
  if (diff < 60) return `${Math.floor(diff)}s ago`
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return `${Math.floor(diff / 86400)}d ago`
}

function statusOf(check) {
  return check.latest?.status || 'never'
}

/**
 * Die feste Sortierung (Plan §2.2 Nr. 5): Status (schlechtester zuerst), dann
 * Risiko, dann der älteste Lauf. Es gibt keine Wahl und deshalb keinen
 * Sortierkopf und keinen `sort`-Parameter (§3.3).
 */
function sortChecks(checks) {
  const riskRank = { high: 0, medium: 1, low: 2 }
  const statusRank = { fail: 0, warn: 1, skipped: 2, ok: 3, never: 4 }
  return [...checks].sort((a, b) => {
    const r = (statusRank[statusOf(a)] ?? 4) - (statusRank[statusOf(b)] ?? 4)
    if (r !== 0) return r
    const rr = (riskRank[a.risk] ?? 3) - (riskRank[b.risk] ?? 3)
    if (rr !== 0) return rr
    const at = a.latest?.ran_at ? new Date(a.latest.ran_at).getTime() : 0
    const bt = b.latest?.ran_at ? new Date(b.latest.ran_at).getTime() : 0
    return at - bt
  })
}

export default function DepHealth() {
  const theme = useTheme()
  const karteStattTabelle = useMediaQuery(theme.breakpoints.down('sm'))

  // 🔴 R16 N1: `null` heisst „noch nie geladen", `[]` hiesse „geladen und
  // leer". Der Unterschied ist der einzige Unterscheider zwischen dem
  // Fehlerzustand und einer ehrlich leeren Liste.
  const [checks, setChecks] = useState(null)
  const [loading, setLoading] = useState(true)
  const [running, setRunning] = useState({})
  const [expanded, setExpanded] = useState({})
  const [expandedData, setExpandedData] = useState({})
  const [error, setError] = useState(null)
  // 🔴 Zwei EIGENE Fehler neben `error` (Regel-15-Runde 1, W4). Der Text
  // „Could not refresh the list." beschreibt den Erstaufbau bzw. das
  // Auffrischen. Ein gescheiterter DETAILABRUF oder eine gescheiterte
  // Zeilenaktion hat eine andere Ursache — die Liste steht ja und wurde
  // gerade gelesen. Beides in `error` zu schreiben liesse die Meldung das
  // Falsche behaupten.
  // 🔴 B3 / W-N4 / W-N5 (Regel-16-Runde 1, Regel-15-Runde 2): der Detailfehler
  // haengt an der ZEILE, nicht an der Seite. Bis hierher war er EIN globaler
  // Platz ueber der Leiste — bei einer Zeile in der dritten Systemtabelle
  // ausserhalb des Sichtfelds, und ein erfolgreicher Abruf einer ANDEREN Zeile
  // loeschte ihn. Die Marke ist je Kennung, damit zwei offene Zeilen zwei
  // Meldungen tragen koennen.
  const [detailError, setDetailError] = useState({})
  // 🔴 W-N7 (Regel-15-Runde 3): `actionError` ist eine Map JE KENNUNG, genau
  // wie `detailError`. Der frühere globale Platz begründete sich damit, „zwei
  // gleichzeitige Aktionen gibt es nicht" — der Kode widerlegt das: `running`
  // ist je Zeile, und deaktiviert wird nur der Knopf DIESER Zeile, also sind
  // zwei Läufe auf zwei Zeilen möglich. Mit einem globalen Platz löschte der
  // Erfolg von Zeile B die Meldung von Zeile A. Jeder Start räumt nur die
  // eigene Kennung; die Meldung nennt zusätzlich die Zeile, weil sie über der
  // Liste steht und nicht in der Zeile selbst.
  const [actionError, setActionError] = useState({})

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await axios.get(`${API}/api/dep-health/list`)
      setChecks(res.data)
      setError(null)
    } catch (e) {
      setError(e.response?.data?.error || e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const loadDetail = useCallback(async (id) => {
    // Ein neuer Versuch raeumt NUR seine eigene Meldung weg.
    setDetailError(d => {
      if (!(id in d)) return d
      const rest = { ...d }
      delete rest[id]
      return rest
    })
    try {
      const res = await axios.get(`${API}/api/dep-health/${id}`)
      setExpandedData(d => ({ ...d, [id]: res.data }))
    } catch (e) {
      setDetailError(d => ({ ...d, [id]: e.response?.data?.error || e.message }))
    }
  }, [])

  // Ein neuer Versuch raeumt NUR seine eigene Meldung weg — dieselbe Regel wie
  // bei `detailError` (W-N7).
  const raeumeAktionsfehler = useCallback((id) => {
    setActionError(m => {
      if (!(id in m)) return m
      const rest = { ...m }
      delete rest[id]
      return rest
    })
  }, [])

  const runOne = async (id, name) => {
    raeumeAktionsfehler(id)
    setRunning(r => ({ ...r, [id]: true }))
    try {
      await axios.post(`${API}/api/dep-health/${id}/run`)
      await load()
      if (expanded[id]) await loadDetail(id)
    } catch (e) {
      setActionError(m => ({ ...m, [id]: { what: 'run', name, message: e.response?.data?.error || e.message } }))
    } finally {
      setRunning(r => ({ ...r, [id]: false }))
    }
  }

  const toggleExpand = async (id) => {
    const next = !expanded[id]
    setExpanded(e => ({ ...e, [id]: next }))
    if (next && !expandedData[id]) await loadDetail(id)
  }

  const markVerified = async (id, name) => {
    raeumeAktionsfehler(id)
    try {
      await axios.post(`${API}/api/dep-health/${id}/mark-verified`, { note: 'Manually verified from dashboard' })
      await load()
      if (expanded[id]) await loadDetail(id)
    } catch (e) {
      setActionError(m => ({ ...m, [id]: { what: 'verify', name, message: e.response?.data?.error || e.message } }))
    }
  }

  // ── Der Zustand der Liste lebt in der ADRESSE (Plan §3.1) ─────────────────
  // `quick` ist die EINZIGE Dimension: kommagetrennt, stabile Werte, leer =
  // keine Einschränkung. `kind: 'quick'` gibt jedem gedrückten Wert seinen
  // EIGENEN Chip (§2.2 Nr. 3, N3.3); mit `multi` entstünde EIN Sammelchip.
  // 🔴 B1 (Regel-16-Runde 1): die Werte tragen ihr ETIKETT. Als blanke
  // Zeichenketten gibt `useListQuery.etikett` (`:353-362`) den Wert selbst
  // zurueck — der Chip hiess dann „Remove filter ok" und die Pille darueber
  // „OK". Ein Zustand, zwei Namen auf einem Bildschirm. Die Objektform ist die
  // dokumentierte Alternative (`useListQuery.test.js`), und der Changelog
  // verspricht bereits „Failing" als Chiptext.
  const dimensionen = useMemo(() => [
    {
      key: 'quick',
      kind: 'quick',
      label: 'Status',
      values: QUICK_VALUES.map((wert) => ({ value: wert, label: QUICK_LABEL[wert] })),
    },
  ], [])
  const query = useListQuery({ dimensions: dimensionen })
  const { state, set, clearAll, chips } = query

  // §5.3b Nr. 4: nach „Clear all" steht der Fokus auf dem ERSTEN Bedienelement
  // von Z1 — und das ist bei C10 die erste Quick-Filter-Pille (es gibt kein
  // Suchfeld). `ActiveFilterBar` schickt den Fokus ins Suchfeld, das hier nicht
  // existiert; ohne diesen Weg fiele er auf `document.body`.
  const quickRef = useRef(null)
  const clearAllMitFokus = useCallback(() => {
    clearAll()
    window.setTimeout(() => {
      const pille = quickRef.current?.querySelector('button[aria-pressed]')
      pille?.focus?.()
    }, 0)
  }, [clearAll])

  // ── Ableitungen ───────────────────────────────────────────────────────────
  const basis = useMemo(() => checks ?? [], [checks])
  const counts = useMemo(() => basis.reduce((acc, c) => {
    const s = statusOf(c)
    acc[s] = (acc[s] || 0) + 1
    return acc
  }, {}), [basis])

  const gefiltert = useMemo(() => {
    const gewaehlt = state.quick ?? []
    if (gewaehlt.length === 0) return basis
    return basis.filter(c => gewaehlt.includes(statusOf(c)))
  }, [basis, state.quick])

  const sortiert = useMemo(() => sortChecks(gefiltert), [gefiltert])

  // 🔴 B2 (Regel-16-Runde 1): die BLOCKORDNUNG haengt am UNGEFILTERTEN Bestand.
  // Vorher entstand sie aus einem `reduce` ueber die gefilterte Liste — die
  // Reihenfolge der Systeme war damit die ihres ersten Treffers, und beim
  // Filtern sprangen die Tabellen um (P18 rot, Meldung „check-07 steht nicht
  // mehr an seiner Stelle"). „Welches System ist das schlimmste" bleibt so
  // auch unter einem Filter ablesbar: der Block behaelt seinen Platz, nur
  // seine Zeilen werden weniger.
  const systemOrdnung = useMemo(() => {
    const ordnung = []
    for (const c of sortChecks(basis)) {
      if (!ordnung.includes(c.system)) ordnung.push(c.system)
    }
    return ordnung
  }, [basis])

  const bySystem = useMemo(() => {
    const gruppen = sortiert.reduce((acc, c) => {
      acc[c.system] = acc[c.system] || []
      acc[c.system].push(c)
      return acc
    }, {})
    // Nur die Systeme, die unter dem Filter noch Zeilen haben — aber in der
    // festen Ordnung. Ein leeres System bekommt keinen leeren Block.
    const geordnet = {}
    for (const system of systemOrdnung) {
      if (gruppen[system]) geordnet[system] = gruppen[system]
    }
    return geordnet
  }, [sortiert, systemOrdnung])

  const sperre = Boolean(error) && checks === null

  const leerGrund = (() => {
    if (sperre) return 'error'
    if (checks === null) return null
    if (checks.length === 0) return 'no-data'
    if (sortiert.length === 0) return 'no-match'
    return null
  })()

  // ── Die Breitenmechanik (G0b) ─────────────────────────────────────────────
  // Gemessen wird der RAUM (der Träger `liste-tabelle`), nicht die gedeckelte
  // Tabelle. Der Rückfall vor der ersten Messung ist das Muster der Seite.
  const tabelleRef = useRef(null)
  const { width: behaelterBreite } = useContainerWidth(tabelleRef)
  const messBasis = behaelterBreite ?? CONTENT_WIDTH.wide ?? CONTENT_WIDTH.reading
  const { visible: sichtbareSpalten } = useColumnPriority({
    columns: SPALTEN,
    containerWidth: messBasis,
  })
  const sichtbar = useMemo(
    () => new Set(sichtbareSpalten.map((s) => s.key)),
    [sichtbareSpalten],
  )
  // 🔴 W1 (Regel-16-Runde 1): das Spaltenbudget wurde bisher BERECHNET und
  // gemeldet, aber nicht angewandt — bei 2560 lagen ~630 px Leerraum zwischen
  // zwei Spalten. Hausform (`design/muster/ListenMuster.jsx:302-313`):
  // `maxWidth = min(Behaelter, Summe der COLUMN_MAX der SICHTBAREN Spalten)`.
  // Kein berechneter 0-Wert: ist das Budget leer, bleibt die Tabelle bei ihrem
  // `minWidth` (ein falscher Deskriptor darf nicht die Tabelle reissen).
  const spaltenBudget = useMemo(
    () => sichtbareSpalten.reduce((summe, sp) => summe + (COLUMN_MAX[sp.kind]?.max ?? 0), 0),
    [sichtbareSpalten],
  )
  const tabellenMax = spaltenBudget > 0 ? Math.min(messBasis, spaltenBudget) : undefined

  // ── Die Bausteine der Leiste ──────────────────────────────────────────────
  // 🔴 Die Zähler sind der GRUNDBESTAND, nicht der gefilterte (§5.2/OE-4):
  // „Failing 3" bleibt 3, auch wenn „Warning" gedrückt ist.
  // 🔴 Im FEHLER tragen die Pillen KEINE Zahl: eine 0 wäre die Behauptung
  // „nichts gefunden", wo „konnte nicht laden" gilt (Regel 22 Nr. 3, P10).
  const quickOptionen = QUICK_VALUES.map((wert) => ({
    value: wert,
    label: QUICK_LABEL[wert],
    ...(sperre ? {} : { count: counts[wert] || 0 }),
  }))

  const quickReihe = (
    <Box ref={quickRef}>
      <FilterPillRow
        multi
        alwaysShow
        options={quickOptionen}
        value={state.quick ?? []}
        onChange={(v) => set('quick', v)}
        disabled={sperre}
      />
    </Box>
  )

  // ── Die Detailfläche einer Prüfung (Plan §1.5: bleibt) ────────────────────
  // 🔴 Die Notiz steht HIER und nicht mehr in der Namensspalte: P19 verlangt,
  // dass der Zeilenbezug im Namen der Zeilenaktion steht, und liest dafür ALLE
  // Textknoten der Kennungsspalte. Zwei Textknoten (Name + Notiz) machen diese
  // Zusicherung unerfüllbar, ohne den Namen zu verunstalten. Die Notiz ist
  // damit nicht weg — sie hat einen Zielort.
  const detailInhalt = (c) => {
    const d = expandedData[c.id]
    // 🔴 B3: der Fehler gehoert HIERHIN, in die aufgeklappte Zeile — nicht an
    // den Seitenkopf und nicht als Dauer-Spinner. `role="alert"` traegt MUI
    // `Alert` selbst; der Knopf ist der zweite Weg zu `loadDetail`, den es
    // vorher nur als Zuklappen/Aufklappen gab (und der nach einem Fehlschlag
    // nicht auffindbar war).
    const fehler = detailError[c.id]
    return (
      <Box sx={{ p: SP.card, bgcolor: 'action.hover' }}>
        {c.notes && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: SP.tight }}>
            {`Note: ${c.notes}`}
          </Typography>
        )}
        {fehler && (
          <Alert
            severity="error"
            data-testid={`detail-fehler-${c.id}`}
            sx={{ mb: SP.tight }}
            action={(
              // 🔴 W10 (Regel-16-Runde 2): ohne Zeilenbezug heissen zwei
              // Knoepfe „Try again" (hier und im Leerzustand) und tun
              // Verschiedenes. Der sichtbare Text bleibt „Try again"
              // (WCAG 2.5.3), der Zugaengliche Name nennt die Zeile.
              <Button
                color="inherit"
                size="small"
                aria-label={`Try again loading the details for ${c.name}`}
                onClick={() => loadDetail(c.id)}
              >
                Try again
              </Button>
            )}
          >
            {`Could not load the details for this check: ${fehler}`}
          </Alert>
        )}
        {!fehler && !d && <CircularProgress size={FS.xl} />}
        {d && (
          <>
            <Typography variant="caption" color="text.secondary">Used in</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: FS.xs, mb: SP.tight }}>
              {(d.used_in || []).join(', ') || '–'}
            </Typography>
            <Typography variant="caption" color="text.secondary">Recent runs</Typography>
            <Table size="small" sx={{ mt: SP.chip }}>
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Latency</TableCell>
                  <TableCell>Streak</TableCell>
                  <TableCell>Detail</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(d.runs || []).map(r => (
                  <TableRow key={r.id}>
                    <TableCell><Typography variant="caption">{formatRelative(r.ran_at)}</Typography></TableCell>
                    <TableCell>
                      <Typography variant="caption">{STATUS_LABEL[r.status] || r.status}</Typography>
                    </TableCell>
                    <TableCell><Typography variant="caption">{r.latency_ms ?? '–'}ms</Typography></TableCell>
                    <TableCell><Typography variant="caption">{r.consecutive_failures || 0}</Typography></TableCell>
                    <TableCell>
                      <Typography variant="caption" color={r.status === 'fail' ? 'error.main' : 'text.secondary'}
                        sx={{ fontFamily: 'monospace', fontSize: FS.xxs, wordBreak: 'break-all' }}>
                        {r.error || r.response_preview || '–'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </Box>
    )
  }

  // ── Die Aktionen einer Zeile: EIN sichtbarer Knopf + ⋮ (§2.2 Nr. 9) ───────
  const aktionen = (c) => {
    const manuell = c.check_type === 'manual'
    const sichtbarer = manuell ? (
      <Button
        size="small"
        aria-label={`Mark verified ${c.name}`}
        onClick={() => markVerified(c.id, c.name)}
      >
        Mark verified
      </Button>
    ) : (
      <Button
        size="small"
        startIcon={running[c.id] ? <CircularProgress size={FS.base} /> : <PlayArrowIcon />}
        aria-label={`Run check ${c.name}`}
        onClick={() => runOne(c.id, c.name)}
        disabled={Boolean(running[c.id])}
      >
        Run
      </Button>
    )
    const weitere = manuell
      ? [{ key: 'run', label: 'Run check', onClick: () => runOne(c.id, c.name), disabled: Boolean(running[c.id]) }]
      : [{ key: 'verify', label: 'Mark verified', onClick: () => markVerified(c.id, c.name) }]
    return { sichtbarer, weitere }
  }

  // 🔴 B4 (Regel-16-Runde 2): der GRUND der Sperre stand nur im
  // `role="none"`-Alert darueber und war damit nicht angesagt. `detail` rendert
  // INNERHALB der einen `role="alert"`-Box (`EmptyState.jsx:265`) — die
  // Ein-Region-Zusage bleibt, und die Ansage traegt Allgemeinsatz UND Grund.
  const leerzustand = leerGrund ? (
    <Box sx={{ py: SP.lg }}>
      <EmptyState
        reason={leerGrund}
        message={
          leerGrund === 'error'
            ? 'Could not load dependency checks.'
            : leerGrund === 'no-data'
              ? 'No dependency checks are registered.'
              : 'No checks match these filters.'
        }
        action={
          leerGrund === 'no-match'
            ? <Button variant="outlined" onClick={clearAllMitFokus}>Clear all filters</Button>
            : undefined
        }
        onRetry={leerGrund === 'error' ? load : undefined}
        detail={leerGrund === 'error' ? error : undefined}
      />
    </Box>
  ) : null

  return (
    <PageContainer width="wide">
      <Box data-testid="liste-kopf">
        <ViewHeader
          title="Dependency Health"
          breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'Ops' }, { label: 'Dependency Health' }]}
        />
      </Box>

      <Box data-testid="liste-panel" aria-busy={loading || undefined}>
        {/* Der GRUND der Sperre steht sichtbar über der Leiste (N8.5/P9).
            🔴 `role="none"` (Regel-16-Runde 1, W4): der Leerzustand darunter
            ist bereits `role="alert"` (`EmptyState.jsx:249`), und zwei
            Live-Regionen sagen denselben Ausfall zweimal an. Der Text bleibt
            im Baum und damit lesbar — angesagt wird er einmal. */}
        {sperre && (
          <Alert severity="error" role="none" sx={{ mb: SP.card }}>
            {error}
          </Alert>
        )}
        {/* Ein gescheitertes AUFFRISCHEN ist nicht der gescheiterte Erstaufbau:
            die Zeilen stehen und bleiben bedienbar. */}
        {!sperre && error && (
          <Alert severity="warning" sx={{ mb: SP.card }}>
            Could not refresh the list. The last loaded rows are still shown.
          </Alert>
        )}
        {/* 🔴 Der DETAILFEHLER steht seit B3 in der Zeile selbst (`detailInhalt`)
            — an seiner Ursache statt am Seitenkopf.
            🔴 `actionError` ist seit W-N7 eine Map je Kennung. Der Platz bleibt
            über der Liste (der kleinere Eingriff), aber JE ZEILE steht ein
            eigener Eintrag: der Erfolg einer Zeile räumt die Meldung einer
            anderen nicht mehr weg. Die Meldung nennt die Zeile, weil sie hier
            oben steht und nicht in der Zeile selbst. */}
        {Object.entries(actionError).map(([id, f]) => (
          <Alert
            key={id}
            severity="error"
            data-testid={`aktions-fehler-${id}`}
            sx={{ mb: SP.card }}
            onClose={() => raeumeAktionsfehler(id)}
          >
            {f.what === 'run'
              ? `Could not run the check ${f.name}: ${f.message}`
              : `Could not mark the check ${f.name} as verified: ${f.message}`}
          </Alert>
        ))}

        <ListToolbar
          quickFilters={quickReihe}
          count={checks === null ? undefined : {
            noun: 'checks',
            n: sortiert.length,
            m: basis.length,
          }}
          refresh={(
            <RefreshButton
              ariaLabel="Refresh dependency health"
              loading={loading}
              firstLoad={loading && checks === null}
              onClick={load}
            />
          )}
          query={{ chips, clearAll: clearAllMitFokus }}
        >
          {query.ignoredValues.length > 0 && (
            <Alert severity="info" data-testid="ignoriert-hinweis" sx={{ mx: SP.card, mt: SP.tight }}>
              {`Ignored unknown filter value “${query.ignoredValues[0].value}”.`}
            </Alert>
          )}

          {/* P18 (eigene Zusicherung, §2.2 Nr. 5): es gibt keine Sortierwahl,
              also keinen Sortierkopf und kein `aria-sort` — der Satz ist der
              einzige Träger der Sortierung. */}
          <Typography
            variant="caption"
            color="text.secondary"
            data-testid="sortiert-nach"
            sx={{ display: 'block', mt: SP.tight }}
          >
            Sorted by status (worst first), then risk, then oldest run.
          </Typography>

          {checks === null && loading && (
            <Box data-testid="liste-skelett" sx={{ mt: SP.tight }}>
              <TableSkeleton columns={8} rows={5} ariaLabel="Loading dependency health" />
            </Box>
          )}

          {leerzustand}

          {!leerzustand && !karteStattTabelle && (
            <Box ref={tabelleRef} data-testid="liste-tabelle" sx={{ mt: SP.tight }}>
              {Object.entries(bySystem).map(([system, systemChecks]) => (
                <Panel key={system} pad="none" clip sx={{ mb: SP.card }}>
                  <Box sx={{ px: SP.card, py: SP.sm, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography
                      variant="subtitle2"
                      data-testid={`system-block-${system}`}
                      sx={{ textTransform: 'uppercase' }}
                    >
                      {system}
                    </Typography>
                  </Box>
                  <TableViewport
                    footerPadPx={0}
                    priorities={SPALTEN}
                    containerWidth={messBasis}
                    stickyFirstColumn
                    stickyColumnIndex={3}
                    edgeHint
                  >
                    <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 860, maxWidth: tabellenMax }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={spaltenBreite('expand')} />
                          <TableCell sx={spaltenBreite('status')}>Status</TableCell>
                          <TableCell sx={spaltenBreite('name')}>Name</TableCell>
                          {sichtbar.has('type') && <TableCell sx={spaltenBreite('type')}>Type</TableCell>}
                          <TableCell sx={spaltenBreite('risk')}>Risk</TableCell>
                          <TableCell sx={spaltenBreite('lastrun')}>Last run</TableCell>
                          <TableCell sx={spaltenBreite('streak')}>Streak</TableCell>
                          <TableCell align="right" sx={spaltenBreite('actions')}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {systemChecks.map((c) => {
                          const { sichtbarer, weitere } = aktionen(c)
                          return (
                            <Fragment key={c.id}>
                              {/* 🔴 Die GANZE Zeile klappt wieder auf (Regel-15
                                  Runde 1, W2): vor dem Umbau hing `onClick` an
                                  sieben Zellen, danach nur noch am Pfeil. Der
                                  TASTATURWEG ist der Pfeil-Knopf — er traegt
                                  `aria-expanded` und den Namen der Pruefung.
                                  🔴 Bewusst KEIN `tabIndex` an der Zeile:
                                  Hausform (`rma/RMACasesDashboardBody.jsx`
                                  §2.7) — zwei fokussierbare Dinge je Zeile, die
                                  dasselbe tun, sind ein zusaetzlicher Tab-Stopp
                                  je Datensatz, ohne einen neuen Weg zu oeffnen. */}
                              <TableRow
                                hover
                                data-testid={`dep-zeile-${c.id}`}
                                onClick={() => toggleExpand(c.id)}
                                sx={{ cursor: 'pointer' }}
                              >
                                <TableCell>
                                  <IconButton
                                    size="small"
                                    aria-expanded={Boolean(expanded[c.id])}
                                    aria-label={`${expanded[c.id] ? 'Collapse' : 'Expand'} ${c.name}`}
                                    // 🔴 `stopPropagation`: ohne sie laufen
                                    // Knopf- UND Zeilenweg im selben Durchlauf.
                                    // Die Zeile geht trotzdem auf (beide lesen
                                    // denselben `expanded`-Stand und schreiben
                                    // `true`), aber `loadDetail` feuert ZWEIMAL
                                    // — `expandedData[id]` ist beim zweiten
                                    // Aufruf noch nicht gesetzt (W-N1,
                                    // Regel-15-Runde 2).
                                    onClick={(e) => { e.stopPropagation(); toggleExpand(c.id) }}
                                  >
                                    {expanded[c.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                  </IconButton>
                                </TableCell>
                                <TableCell sx={spaltenBreite('status')}>
                                  <StatusAnzeige status={c.latest?.status} />
                                </TableCell>
                                <TableCell sx={spaltenBreite('name')}>
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: FS.xs }}>
                                    {c.name}
                                  </Typography>
                                </TableCell>
                                {sichtbar.has('type') && (
                                  <TableCell sx={spaltenBreite('type')}>
                                    <Typography variant="caption">{c.check_type}</Typography>
                                  </TableCell>
                                )}
                                <TableCell sx={spaltenBreite('risk')}>
                                  <Typography variant="caption" color={RISK_COLOR[c.risk] || 'text.secondary'}>
                                    {RISK_LABEL[c.risk] || c.risk}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={spaltenBreite('lastrun')}>
                                  {/* 🔴 W5: der genaue Zeitpunkt haengt nicht
                                      mehr NUR am Tooltip. Ein Tooltip ist kein
                                      Tastaturweg — wer nur die Tastatur hat,
                                      hoerte „80d ago" und nie den Zeitpunkt.
                                      Der `title` bleibt (Maus), der
                                      `visuallyHidden`-Text traegt denselben
                                      Wert fuer die Vorlesehilfe. */}
                                  <Tooltip title={c.latest?.ran_at || 'never'}>
                                    <Typography variant="caption">
                                      {formatRelative(c.latest?.ran_at)}
                                      {/* 🔴 W8 (Regel-16-Runde 2): die Ergaenzung
                                          nur mit Zeitpunkt — sonst las die
                                          Vorlesehilfe „never (never)". */}
                                      {c.latest?.ran_at && (
                                        <Box component="span" sx={visuallyHidden}>
                                          {` (${c.latest.ran_at})`}
                                        </Box>
                                      )}
                                    </Typography>
                                  </Tooltip>
                                  {c.latest?.latency_ms != null && (
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: SP.chip }}>
                                      {`(${c.latest.latency_ms}ms)`}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell sx={spaltenBreite('streak')}>
                                  <Typography variant="caption">
                                    {c.latest?.streak > 0 ? `${c.latest.streak}x` : '–'}
                                  </Typography>
                                </TableCell>
                                {/* 🔴 Die Aktionszelle stoppt den Klick: „Run" und
                                    der ⋮ duerfen die Zeile NICHT mit aufklappen
                                    (Regel-15 C2, sonst bricht P19s Trennung von
                                    Handlung und Zeile). */}
                                <TableCell
                                  align="right"
                                  sx={spaltenBreite('actions')}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Stack direction="row" spacing={SP.chip} justifyContent="flex-end" alignItems="center">
                                    {sichtbarer}
                                    <RowActionsMenu label={`Actions for ${c.name}`} actions={weitere} />
                                  </Stack>
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell colSpan={sichtbar.has('type') ? 8 : 7} sx={{ p: 0, border: 'none' }}>
                                  <Collapse in={Boolean(expanded[c.id])}>
                                    {detailInhalt(c)}
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            </Fragment>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </TableViewport>
                </Panel>
              ))}
            </Box>
          )}

          {!leerzustand && karteStattTabelle && (
            <Box data-testid="liste-karte" sx={{ mt: SP.tight }}>
              <Stack spacing={SP.tight}>
                {sortiert.map((c) => {
                  const { sichtbarer, weitere } = aktionen(c)
                  return (
                    <MobileRecordCard
                      key={c.id}
                      data-testid={`dep-karte-${c.id}`}
                      slotOrder="chips-first"
                      title={c.name}
                      meta={(
                        <Typography variant="caption" color="text.secondary">
                          {`${c.system} · ${c.check_type}`}
                        </Typography>
                      )}
                      // 🔴 `fieldTestIds` stempelt die FELDPLAETZE des Bausteins
                      // (`title`, `id`, `meta`, `chips`, `actions`, `leading`,
                      // `select` — `MobileRecordCard.jsx:234-236`). Plan §2.2
                      // Nr. 10 nennt `status`/`risk`/`lastrun`; das sind KEINE
                      // Plaetze, sondern Inhalte von `chips`, und `lastrun` steht
                      // auf der Karte gar nicht (die Karte traegt Status · Risk ·
                      // Streak). Deshalb: die zwei Plaetze hier, die Inhalte ueber
                      // eigene Marken (`karte-status-*`, `karte-risk-*`) — die
                      // Abweichung ist die Bauform des Bausteins, nicht eine
                      // Auslassung.
                      fieldTestIds={{
                        meta: `karte-kennung-${c.id}`,
                        chips: `karte-chips-${c.id}`,
                      }}
                      chips={(
                        <Stack direction="row" spacing={SP.chip} useFlexGap flexWrap="wrap" alignItems="center">
                          <Box component="span" data-testid={`karte-status-${c.id}`}>
                            <StatusAnzeige status={c.latest?.status} />
                          </Box>
                          <Box component="span" data-testid={`karte-risk-${c.id}`}>
                            <Typography variant="caption" color="text.secondary">
                              {`Risk ${RISK_LABEL[c.risk] || c.risk}`}
                            </Typography>
                          </Box>
                          {c.latest?.streak > 0 && (
                            <Typography variant="caption" color="text.secondary">{`Streak ${c.latest.streak}x`}</Typography>
                          )}
                        </Stack>
                      )}
                      actions={(
                        <Stack direction="row" spacing={SP.chip} alignItems="center">
                          <IconButton
                            size="small"
                            aria-expanded={Boolean(expanded[c.id])}
                            aria-label={`${expanded[c.id] ? 'Collapse' : 'Expand'} ${c.name}`}
                            onClick={() => toggleExpand(c.id)}
                          >
                            {expanded[c.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                          {sichtbarer}
                          {/* 🔴 W-N8: Eigenschaftsreihenfolge gedreht, damit
                              diese Zeile sich von der Tabelle (gleicher Text,
                              andere Einrückung) auch INHALTLICH unterscheidet
                              — die Rotweg-TSV spricht genau eine Dateizeile
                              an. Verhalten unverändert. */}
                          <RowActionsMenu actions={weitere} label={`Actions for ${c.name}`} />
                        </Stack>
                      )}
                      expanded={expanded[c.id] ? detailInhalt(c) : undefined}
                    />
                  )
                })}
              </Stack>
            </Box>
          )}
        </ListToolbar>
      </Box>
    </PageContainer>
  )
}
